import { memo, useCallback, useMemo } from 'react';

import { Button } from '@air/primitive-button';

import { LocationsFilterBoardTree } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTree';
import { type LocationsFilterBoardTreeItemProps } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTreeItem';
import { LocationsFilterLoadingSkeleton } from '~/components/Filters/LocationsFilter/LocationsFilterLoadingSkeleton';
import { useLocationsFilterData } from '~/components/Filters/LocationsFilter/useLocationsFilterData';

export type LocationsFilterRootBoardTreeProps = Pick<LocationsFilterBoardTreeItemProps, 'onBoardSelect'> & {
  search: string | undefined;
};

export const LocationsFilterRootBoardTree = memo(({ search, onBoardSelect }: LocationsFilterRootBoardTreeProps) => {
  const {
    boards: boardsMap,
    hasMoreBoards,
    loadMoreBoards,
    isLoadingMoreBoards,
    isLoading,
  } = useLocationsFilterData({ search });
  const boards = useMemo(() => Array.from(boardsMap.values()), [boardsMap]);

  const handleLoadMoreBoards = useCallback(() => {
    loadMoreBoards();
  }, [loadMoreBoards]);

  if (isLoading) return <LocationsFilterLoadingSkeleton />;

  if (search && boards.length === 0) {
    return <p className="px-2 py-1 text-12 text-grey-10">No results</p>;
  }

  return (
    <div data-testid="LOCATIONS_FILTER_ROOT_BOARD_TREE">
      <LocationsFilterBoardTree boards={boards} onBoardSelect={onBoardSelect} />

      {!search && hasMoreBoards && (
        <Button
          appearance="ghost"
          className="ml-6 justify-start"
          color="grey"
          data-testid="LOCATIONS_FILTER_SHOW_MORE"
          isLoading={isLoadingMoreBoards}
          onClick={handleLoadMoreBoards}
          size="small"
        >
          Show more
        </Button>
      )}
    </div>
  );
});

LocationsFilterRootBoardTree.displayName = 'LocationsFilterRootBoardTree';
